import React, { useEffect, useState } from 'react';
import { Save, Bell, DollarSign, Building2, Loader2, Cloud, CloudOff } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
interface Settings {
  companyName: string;
  email: string;
  phone: string;
  address: string;
  currency: string;
  taxRate: string;
  enableNotifications: boolean;
  enableEmailAlerts: boolean;
  autoAcceptOrders: boolean;
  requireOrderConfirmation: boolean;
}
const DEFAULT_SETTINGS: Settings = {
  companyName: 'Criket',
  email: 'admin@criket.com',
  phone: '809-555-1234',
  address: 'Santo Domingo, República Dominicana',
  currency: 'RD$',
  taxRate: '18',
  enableNotifications: true,
  enableEmailAlerts: true,
  autoAcceptOrders: false,
  requireOrderConfirmation: true
};
export function SettingsGeneral() {
  const {
    showToast
  } = useToast();
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState<'supabase' | 'local'>('local');
  // Load settings from Supabase first, fallback to localStorage
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        if (isSupabaseConfigured()) {
          const dbSettings = await supabaseClient.getSettings('general');
          if (dbSettings) {
            setSettings({
              ...DEFAULT_SETTINGS,
              ...dbSettings
            });
            setDataSource('supabase');
            // Also sync to localStorage as cache
            localStorage.setItem('criket_general_settings', JSON.stringify({
              ...DEFAULT_SETTINGS,
              ...dbSettings
            }));
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error loading settings from Supabase:', error);
      }
      // Fallback to localStorage
      const saved = localStorage.getItem('criket_general_settings');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSettings({
            ...DEFAULT_SETTINGS,
            ...parsed
          });
        } catch (e) {
          setSettings(DEFAULT_SETTINGS);
        }
      }
      setDataSource('local');
      setLoading(false);
    };
    loadSettings();
  }, []);
  const handleSave = async () => {
    if (!settings.companyName.trim()) {
      showToast('warning', 'Campo requerido', 'El nombre de la empresa no puede estar vacío');
      return;
    }
    if (!settings.email.trim()) {
      showToast('warning', 'Campo requerido', 'El correo electrónico no puede estar vacío');
      return;
    }
    setSaving(true);
    try {
      // Always save to localStorage as cache
      localStorage.setItem('criket_general_settings', JSON.stringify(settings));
      // Try to save to Supabase
      if (isSupabaseConfigured()) {
        await supabaseClient.saveSettings('general', settings);
        setDataSource('supabase');
        showToast('success', 'Configuración guardada', 'Los cambios se han guardado en la base de datos');
      } else {
        setDataSource('local');
        showToast('success', 'Guardado localmente', 'Los cambios se guardaron localmente. Conecta Supabase para persistencia en la nube.');
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      // Still saved to localStorage
      setDataSource('local');
      showToast('warning', 'Guardado parcial', 'Se guardó localmente pero no se pudo conectar con la base de datos. Verifica que la tabla "settings" exista en Supabase.');
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center py-20" data-id="element-5724">
        <div className="text-center" data-id="element-5725">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" data-id="element-5726" />
          <p className="text-sm text-gray-500" data-id="element-5727">Cargando configuración...</p>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5728">
      {/* Data source indicator */}
      <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium ${dataSource === 'supabase' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'}`} data-id="element-5729">
        {dataSource === 'supabase' ? <>
            <Cloud size={14} data-id="element-5730" />
            Conectado a base de datos (Supabase)
          </> : <>
            <CloudOff size={14} data-id="element-5731" />
            Almacenamiento local — los datos no se sincronizan entre
            dispositivos
          </>}
      </div>

      {/* Company Information */}
      <Card data-id="element-5732">
        <div className="flex items-center gap-3 mb-6" data-id="element-5733">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-5734">
            <DollarSign className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-5735" />
          </div>
          <div data-id="element-5736">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5737">
              Información de la Empresa
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5738">
              Datos generales de tu negocio
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5739">
          <div data-id="element-5740">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5741">
              Nombre de la Empresa <span className="text-red-500" data-id="element-5742">*</span>
            </label>
            <input type="text" value={settings.companyName} onChange={e => setSettings({
            ...settings,
            companyName: e.target.value
          })} placeholder="Ej: Criket" className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${!settings.companyName.trim() ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-700 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white'}`} data-id="element-5743" />
            {!settings.companyName.trim() && <p className="text-xs text-red-500 mt-1" data-id="element-5744">
                Este campo es obligatorio
              </p>}
          </div>
          <div data-id="element-5745">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5746">
              Correo Electrónico <span className="text-red-500" data-id="element-5747">*</span>
            </label>
            <input type="email" value={settings.email} onChange={e => setSettings({
            ...settings,
            email: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5748" />
          </div>
          <div data-id="element-5749">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5750">
              Teléfono
            </label>
            <input type="tel" value={settings.phone} onChange={e => setSettings({
            ...settings,
            phone: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5751" />
          </div>
          <div data-id="element-5752">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5753">
              Dirección
            </label>
            <input type="text" value={settings.address} onChange={e => setSettings({
            ...settings,
            address: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5754" />
          </div>
        </div>
      </Card>

      {/* Financial Settings */}
      <Card data-id="element-5755">
        <div className="flex items-center gap-3 mb-6" data-id="element-5756">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-5757">
            <DollarSign className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-5758" />
          </div>
          <div data-id="element-5759">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5760">
              Configuración Financiera
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5761">
              Moneda e impuestos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5762">
          <div data-id="element-5763">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5764">
              Moneda
            </label>
            <select value={settings.currency} onChange={e => setSettings({
            ...settings,
            currency: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5765">
              <option value="RD$" data-id="element-5766">Peso Dominicano (RD$)</option>
              <option value="USD" data-id="element-5767">Dólar Americano (USD)</option>
            </select>
          </div>
          <div data-id="element-5768">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5769">
              Tasa de ITBIS (%)
            </label>
            <input type="number" value={settings.taxRate} onChange={e => setSettings({
            ...settings,
            taxRate: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-5770" />
          </div>
        </div>
      </Card>

      {/* Notifications */}
      <Card data-id="element-5771">
        <div className="flex items-center gap-3 mb-6" data-id="element-5772">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-5773">
            <Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" data-id="element-5774" />
          </div>
          <div data-id="element-5775">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5776">
              Notificaciones
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5777">
              Configura alertas y notificaciones
            </p>
          </div>
        </div>

        <div className="space-y-4" data-id="element-5778">
          <label className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-5779">
            <div className="min-w-0" data-id="element-5780">
              <p className="font-medium text-gray-900 dark:text-white" data-id="element-5781">
                Notificaciones Push
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5782">
                Recibe alertas en tiempo real
              </p>
            </div>
            <input type="checkbox" checked={settings.enableNotifications} onChange={e => setSettings({
            ...settings,
            enableNotifications: e.target.checked
          })} className="w-5 h-5 shrink-0 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-5783" />
          </label>

          <label className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-5784">
            <div className="min-w-0" data-id="element-5785">
              <p className="font-medium text-gray-900 dark:text-white" data-id="element-5786">
                Alertas por Email
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5787">
                Recibe resúmenes diarios por correo
              </p>
            </div>
            <input type="checkbox" checked={settings.enableEmailAlerts} onChange={e => setSettings({
            ...settings,
            enableEmailAlerts: e.target.checked
          })} className="w-5 h-5 shrink-0 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-5788" />
          </label>

          <label className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-5789">
            <div className="min-w-0" data-id="element-5790">
              <p className="font-medium text-gray-900 dark:text-white" data-id="element-5791">
                Auto-aceptar Pedidos
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5792">
                Acepta pedidos automáticamente sin confirmación
              </p>
            </div>
            <input type="checkbox" checked={settings.autoAcceptOrders} onChange={e => setSettings({
            ...settings,
            autoAcceptOrders: e.target.checked
          })} className="w-5 h-5 shrink-0 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-5793" />
          </label>

          <label className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-5794">
            <div className="min-w-0" data-id="element-5795">
              <p className="font-medium text-gray-900 dark:text-white" data-id="element-5796">
                Requerir Confirmación
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5797">
                Los empleados deben confirmar sus pedidos
              </p>
            </div>
            <input type="checkbox" checked={settings.requireOrderConfirmation} onChange={e => setSettings({
            ...settings,
            requireOrderConfirmation: e.target.checked
          })} className="w-5 h-5 shrink-0 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-5798" />
          </label>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end" data-id="element-5799">
        <Button onClick={handleSave} leftIcon={saving ? <Loader2 size={18} className="animate-spin" data-id="element-5801" /> : <Save size={18} data-id="element-5802" />} size="lg" disabled={saving} data-id="element-5800">
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>
    </div>;
}